import type { RouterProps } from 'wouter';
import { PaymentsLayout } from 'components/Layout/PaymentsLayout';
import { AccountDetailPage } from 'components/pages/payments/AccountDetailPage';
import { CatalogPage } from 'components/pages/payments/CatalogPage';
import { CreateInvoicePage } from 'components/pages/payments/CreateInvoicePage';
import { InvoiceDetailPage } from 'components/pages/payments/InvoiceDetailPage';
import { InvoicesListPage } from 'components/pages/payments/InvoicesListPage';
import { PaymentsPage } from 'components/pages/payments/PaymentsPage';
import { PaymentsSettingsPage } from 'components/pages/payments/PaymentsSettingsPage';
import { TransactionDetailPage } from 'components/pages/payments/TransactionDetailPage';
import { TransactionListPage } from 'components/pages/payments/TransactionListPage';
import { UserDetailPage } from 'components/pages/payments/UserDetailPage';
import { UsersListPage } from 'components/pages/payments/UsersListPage';
import { useFlagEnabledWithOverrides } from 'hooks/useFlagEnabledWithOverrides';
import React, { Suspense } from 'react';
import { makePath } from 'utils/routes';
import { Redirect, Route, Router, Switch } from 'wouter';
import { usePaymentsApiHttpClientContext } from 'wrappers/PaymentsApiAuthProvider';

import { DecodedParamsRoute } from './DecodedParamsRoute';

export const PaymentsRouter = ({
  hook,
  base,
  workspaceId,
}: Partial<RouterProps> & { workspaceId: string }): ReturnType<
  typeof Router
> => {
  const isPaymentsForRealEnabled = useFlagEnabledWithOverrides(
    'dashboard-payments-for-real'
  );
  const { hasWorkspace } = usePaymentsApiHttpClientContext();
  if (!hasWorkspace) {
    return (
      <PaymentsLayout>
        <>Loading...</>
      </PaymentsLayout>
    );
  }
  return (
    <PaymentsLayout>
      <Suspense fallback={<>Loading...</>}>
        <Router hook={hook} base={base} key={base}>
          <Switch>
            <DecodedParamsRoute path="/users/:externalId">
              {(params) => (
                <UserDetailPage
                  workspaceId={workspaceId}
                  externalId={params.externalId}
                />
              )}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/invoices/:invoiceId">
              {(params) => (
                <InvoiceDetailPage
                  workspaceId={workspaceId}
                  invoiceId={params.invoiceId}
                />
              )}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/invoices">
              <InvoicesListPage workspaceId={workspaceId} />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/accounts/:accountId">
              {(params) => (
                <AccountDetailPage
                  workspaceId={workspaceId}
                  accountId={params.accountId}
                />
              )}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/transactions/:transactionId">
              {(params) => (
                <TransactionDetailPage
                  workspaceId={workspaceId}
                  transactionId={params.transactionId}
                />
              )}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/transactions">
              <TransactionListPage workspaceId={workspaceId} />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/users">
              <UsersListPage workspaceId={workspaceId} />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/catalog">
              <CatalogPage workspaceId={workspaceId} />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/create-invoice">
              <CreateInvoicePage workspaceId={workspaceId} />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/settings/:tab">
              {(params) => <PaymentsSettingsPage tab={params.tab} />}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/settings">
              <PaymentsSettingsPage />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/payment-settings">
              {isPaymentsForRealEnabled ? <PaymentsPage /> : null}
            </DecodedParamsRoute>
            <Route>
              <Redirect
                to={makePath(['w', workspaceId, 'payments', 'invoices'], {
                  absolute: true,
                })}
              />
            </Route>
          </Switch>
        </Router>
      </Suspense>
    </PaymentsLayout>
  );
};
